import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { LocationTag, MediaOverlays, Sport, User } from '../types';
import MediaEditor from './MediaEditor';
import OverlayLayer from './OverlayLayer';
import { hasOverlayContent } from '../utils/overlays';
import { SPORTS } from '../constants';
import XMarkIcon from './icons/XMarkIcon';
import CameraIcon from './icons/CameraIcon';
import MapPinIcon from './icons/MapPinIcon';
import { readVideoMetadata } from '../utils/mediaUtils';

const POST_VIDEO_MAX_SECONDS = 900; // 15 minutes
const MAX_POST_MEDIA = 10;

const formatBytes = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;

const FIELD_LABEL = 'mb-2 block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400';
const FIELD_INPUT = 'w-full rounded-none border-0 border-b border-white/15 bg-transparent px-0 py-3 text-base text-white placeholder-gray-500 transition focus:border-lime-400 focus:outline-none focus:ring-0';

const ImagesIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="3" y="5" width="18" height="14" rx="3" />
    <circle cx="9" cy="10.5" r="1.6" />
    <path d="m21 16-5-4.5L8 19" />
  </svg>
);

export interface UploadPostDraft {
  mediaItems: { file: File; previewUrl: string }[];
  caption: string;
  tricks: string[];
  sport: Sport;
  locationTag: LocationTag | null;
  // Text/drawing edits, keyed by the media item's preview URL.
  overlays?: Record<string, MediaOverlays>;
}

interface UploadPostPageProps {
  currentUser: User | null;
  allUsers: User[];
  onPromptLogin: () => void;
  onAddPost: (postData: { mediaFiles: File[]; caption: string; sport: Sport; tricks?: string[]; locationTag?: LocationTag | null; overlays?: (MediaOverlays | null)[] }) => Promise<void>;
  onClose: () => void;
  initialDraft?: UploadPostDraft | null;
  onRequestLocationPick: (draft: UploadPostDraft) => void;
}

const UploadPostPage: React.FC<UploadPostPageProps> = ({ currentUser, allUsers, onPromptLogin, onAddPost, onClose, initialDraft, onRequestLocationPick }) => {
  const [mediaItems, setMediaItems] = useState<{ file: File; previewUrl: string }[]>(() => initialDraft?.mediaItems ?? []);
  // The first photo or video sets the size (shape) of the whole post; everything added after it is cropped to fit that same frame.
  const [frameRatio, setFrameRatio] = useState<number | null>(null);
  const firstMedia = mediaItems[0];
  useEffect(() => {
    let cancelled = false;
    setFrameRatio(null);
    if (!firstMedia) return;
    const done = (w: number, h: number) => { if (!cancelled && w > 0 && h > 0) setFrameRatio(Math.min(1.91, Math.max(0.56, w / h))); };
    if (firstMedia.file.type.startsWith('video/')) {
      readVideoMetadata(firstMedia.file).then((m) => done(m.width, m.height)).catch(() => undefined);
    } else {
      const img = new Image();
      img.onload = () => done(img.naturalWidth, img.naturalHeight);
      img.src = firstMedia.previewUrl;
    }
    return () => { cancelled = true; };
  }, [firstMedia?.previewUrl]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [newPostCaption, setNewPostCaption] = useState(() => initialDraft?.caption ?? '');
  const [newPostTricks, setNewPostTricks] = useState<string[]>(() => initialDraft?.tricks ?? []);
  const [newPostTrickInput, setNewPostTrickInput] = useState('');
  const [newPostSport, setNewPostSport] = useState<Sport>(() => initialDraft?.sport ?? SPORTS[0]);
  const [locationTag, setLocationTag] = useState<LocationTag | null>(() => initialDraft?.locationTag ?? null);
  const [overlaysByUrl, setOverlaysByUrl] = useState<Record<string, MediaOverlays>>(() => initialDraft?.overlays ?? {});
  const [isEditing, setIsEditing] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const captionRef = useRef<HTMLTextAreaElement>(null);

  const mentionResults = useMemo(() => {
    if (!mentionQuery) return [];
    return allUsers
      .filter((user) => user.id !== currentUser?.id)
      .filter((user) => user.username.toLowerCase().includes(mentionQuery.toLowerCase()))
      .slice(0, 6);
  }, [allUsers, currentUser?.id, mentionQuery]);

  const handleImageSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files: File[] = Array.from(event.target.files ?? []);
    event.target.value = '';
    if (!files.length) return;
    setError('');
    const added: { file: File; previewUrl: string }[] = [];
    for (const file of files) {
      if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
        setError('Please choose photos or videos.');
        continue;
      }
      if (file.type.startsWith('video/')) {
        try {
          const { duration } = await readVideoMetadata(file);
          if (duration > POST_VIDEO_MAX_SECONDS) {
            setError(`A video was skipped: it is ${Math.round(duration / 60)} minutes long. Videos can be up to 15 minutes.`);
            continue;
          }
        } catch {
          // Can't read metadata — let it through.
        }
      }
      // (Not revoked on unmount: the draft handed to the location picker keeps using these preview URLs.)
      added.push({ file, previewUrl: URL.createObjectURL(file) });
    }
    if (added.length) {
      setMediaItems((prev) => {
        const next = [...prev, ...added].slice(0, MAX_POST_MEDIA);
        if (prev.length + added.length > MAX_POST_MEDIA) setError(`Posts can have up to ${MAX_POST_MEDIA} photos or videos.`);
        return next;
      });
    }
  };

  const removeMedia = (index: number) => {
    setMediaItems((prev) => prev.filter((_, i) => i !== index));
    setActiveIdx((cur) => Math.max(0, Math.min(cur, mediaItems.length - 2)));
  };

  const handleCreatePost = () => {
    if (!currentUser) { onPromptLogin(); return; }
    if (!mediaItems.length || isPosting) return;
    setIsPosting(true);
    // Fire the upload in the background and leave immediately — App.tsx tracks
    // upload progress and shows it on the feed instead of blocking this screen.
    onAddPost({
      mediaFiles: mediaItems.map((m) => m.file),
      caption: newPostCaption,
      tricks: newPostTricks,
      sport: newPostSport,
      locationTag,
      overlays: mediaItems.map((m) => (hasOverlayContent(overlaysByUrl[m.previewUrl]) ? overlaysByUrl[m.previewUrl] : null)),
    }).catch((err) => {
      console.error('Failed to create post', err);
    });
    onClose();
  };

  const handleCaptionChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    const caret = event.target.selectionStart ?? value.length;
    setNewPostCaption(value);
    const prefix = value.slice(0, caret);
    const match = prefix.match(/(?:^|\s)@([A-Za-z0-9_]*)$/);
    setMentionQuery(match ? match[1] : '');
  };

  const handleMentionSelect = (username: string) => {
    const textarea = captionRef.current;
    if (!textarea) return;
    const caret = textarea.selectionStart ?? newPostCaption.length;
    const prefix = newPostCaption.slice(0, caret);
    const suffix = newPostCaption.slice(caret);
    const nextPrefix = prefix.replace(/(^|\s)@([A-Za-z0-9_]*)$/, (_match, leadingSpace) => `${leadingSpace}@${username} `);
    const nextValue = `${nextPrefix}${suffix}`;
    setNewPostCaption(nextValue);
    setMentionQuery('');
    requestAnimationFrame(() => {
      const nextCaret = nextPrefix.length;
      textarea.focus();
      textarea.setSelectionRange(nextCaret, nextCaret);
    });
  };

  const addTrick = (rawTrick: string) => {
    const trick = rawTrick.trim();
    if (!trick) return;
    setNewPostTricks(prev => {
      if (prev.some(e => e.toLowerCase() === trick.toLowerCase())) return prev;
      return [...prev, trick].slice(0, 12);
    });
    setNewPostTrickInput('');
  };

  const removeTrick = (trickToRemove: string) => {
    setNewPostTricks(prev => prev.filter(t => t !== trickToRemove));
  };

  const mediaItem = mediaItems[Math.min(activeIdx, mediaItems.length - 1)] ?? null;
  const isVideo = mediaItem?.file.type.startsWith('video/') ?? false;

  return (
    <div className="ridaz-fullpage fixed inset-0 z-[6000] flex flex-col bg-app-backdrop text-white">
      <input type="file" ref={fileInputRef} onChange={handleImageSelect} accept="image/*,video/*" multiple className="hidden" />

      {/* Top bar */}
      <header
        className="flex items-center gap-3 border-b border-white/10 bg-black px-4 pb-3"
        style={{ paddingTop: 'calc(var(--sat-native, env(safe-area-inset-top)) + 0.75rem)' }}
      >
        <button
          onClick={onClose}
          className="flex h-10 w-10 items-center justify-center transition hover:bg-white/10"
          aria-label="Close"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-black leading-tight">New post</h1>
      </header>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto w-full max-w-[560px] space-y-6 px-4 py-5">
          {!currentUser && (
            <div className="flex items-center justify-between gap-3 rounded-lg bg-white/[0.07] px-4 py-3">
              <p className="text-sm font-semibold text-white">Sign in to share a post.</p>
              <button onClick={onPromptLogin} className="shrink-0 rounded-lg bg-white px-3 py-1.5 text-xs font-black uppercase text-black hover:bg-gray-200">Sign in</button>
            </div>
          )}

          {/* Media */}
          {mediaItem ? (
            <section aria-label="Preview" className="space-y-3">
              <div
                className="relative mx-auto overflow-hidden rounded-lg bg-black"
                style={frameRatio ? { aspectRatio: String(frameRatio), width: `min(100%, ${(52 * frameRatio).toFixed(1)}vh)` } : undefined}
              >
                {isVideo ? (
                  <video key={mediaItem.previewUrl} src={mediaItem.previewUrl} className={frameRatio ? 'h-full w-full object-cover' : 'max-h-[48vh] w-full object-contain'} controls playsInline />
                ) : (
                  <img src={mediaItem.previewUrl} alt="Post preview" className={frameRatio ? 'h-full w-full object-cover' : 'max-h-[48vh] w-full object-contain'} />
                )}
                <OverlayLayer overlays={overlaysByUrl[mediaItem.previewUrl]} fit={frameRatio ? 'cover' : 'contain'} />
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className={`absolute bottom-3 right-3 z-[3] flex items-center gap-2.5 rounded-2xl bg-lime-400 px-5 py-3 text-base font-black text-black shadow-[0_8px_24px_rgba(0,0,0,0.5)] ring-2 ring-white/40 transition active:scale-95`}
                  aria-label="Edit with text and drawing"
                >
                  <svg viewBox="0 0 24 24" className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round"><path d="M4 20l1-4L16.5 4.5a2.1 2.1 0 0 1 3 3L8 19z" /><path d="M14.5 6.5l3 3" /></svg>
                  {hasOverlayContent(overlaysByUrl[mediaItem.previewUrl]) ? 'Edited ✓' : 'Text & draw'}
                </button>
                <div className="pointer-events-none absolute left-3 top-3 z-[3] flex gap-1.5">
                  <span className="rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-bold uppercase tracking-wide backdrop-blur">{isVideo ? 'Video' : 'Photo'}</span>
                  <span className="rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-bold text-gray-200 backdrop-blur">{formatBytes(mediaItem.file.size)}</span>
                  {mediaItems.length > 1 && <span className="rounded-full bg-black/70 px-2.5 py-1 text-[11px] font-bold text-gray-200 backdrop-blur">{Math.min(activeIdx, mediaItems.length - 1) + 1}/{mediaItems.length}</span>}
                </div>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:none]">
                {mediaItems.map((m, i) => (
                  <div key={m.previewUrl} className={`relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border-2 ${i === Math.min(activeIdx, mediaItems.length - 1) ? 'border-lime-400' : 'border-transparent'}`}>
                    <button type="button" onClick={() => setActiveIdx(i)} className="h-full w-full" aria-label={`Show item ${i + 1}`}>
                      {m.file.type.startsWith('video/')
                        ? <video src={`${m.previewUrl}#t=0.001`} className="h-full w-full object-cover" muted playsInline preload="metadata" />
                        : <img src={m.previewUrl} alt="" className="h-full w-full object-cover" />}
                    </button>
                    <button type="button" onClick={() => removeMedia(i)} className="absolute right-0.5 top-0.5 flex h-5 w-5 items-center justify-center rounded-full bg-black/75" aria-label={`Remove item ${i + 1}`}>
                      <XMarkIcon className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {mediaItems.length < MAX_POST_MEDIA && (
                  <button type="button" onClick={() => fileInputRef.current?.click()} className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg bg-white/[0.07] text-2xl font-bold text-gray-300 hover:bg-white/10" aria-label="Add more media">+</button>
                )}
              </div>
              <p className="rounded-xl bg-white/[0.06] px-3.5 py-2.5 text-[13px] font-semibold leading-snug text-gray-300">
                The first photo or video sets the size of your post. Anything you add after it is cropped to match, so every slide stays the same size.
              </p>
            </section>
          ) : (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex aspect-[4/3] w-full flex-col items-center justify-center gap-3 rounded-lg bg-white/[0.05] transition hover:bg-white/[0.08] active:scale-[0.99]"
              aria-label="Select photos or videos"
            >
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/10">
                <ImagesIcon className="h-8 w-8 text-white" />
              </span>
              <span className="text-base font-black">Add photos or videos</span>
            </button>
          )}

          {error && (
            <p role="alert" className="rounded-2xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm font-semibold text-red-200">{error}</p>
          )}

          {/* Caption */}
          <div className="relative">
            <label htmlFor="caption" className={FIELD_LABEL}>Caption</label>
            <textarea
              ref={captionRef}
              id="caption"
              value={newPostCaption}
              onChange={handleCaptionChange}
              rows={3}
              placeholder="Write a caption… use @ to tag riders"
              className={`${FIELD_INPUT} resize-none`}
            />
            {mentionResults.length > 0 && (
              <div className="absolute left-0 right-0 top-full z-20 mt-2 overflow-hidden rounded-xl bg-[#1c1d20] shadow-2xl shadow-black/60">
                {mentionResults.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => handleMentionSelect(user.username)}
                    className="flex w-full items-center gap-3 px-3 py-2.5 text-left transition-colors hover:bg-white/10"
                  >
                    <img src={user.avatarUrl} alt="" className="h-9 w-9 rounded-lg border border-white/10 object-cover" />
                    <div className="min-w-0">
                      <p className="truncate text-sm font-bold">{'@' + String(user.username).toUpperCase()}</p>
                      {(user.firstName || user.lastName) && (
                        <p className="truncate text-xs text-gray-400">{[user.firstName, user.lastName].filter(Boolean).join(' ')}</p>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Sport */}
          <div>
            <span className={FIELD_LABEL}>Sport</span>
            <div className="flex flex-wrap gap-2" role="radiogroup" aria-label="Sport">
              {SPORTS.map((sport) => {
                const selected = newPostSport.id === sport.id;
                return (
                  <button
                    type="button"
                    key={sport.id}
                    role="radio"
                    aria-checked={selected}
                    onClick={() => setNewPostSport(sport)}
                    className={`rounded-full border px-3.5 py-1.5 text-sm font-semibold transition ${
                      selected ? 'border-lime-400 bg-lime-400 text-black' : 'border-transparent bg-white/[0.07] text-gray-200 hover:bg-white/10'
                    }`}
                  >
                    {sport.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tricks */}
          <div>
            <label htmlFor="post-tricks" className={FIELD_LABEL}>Tricks <span className="font-medium normal-case tracking-normal text-gray-500">(optional)</span></label>
            <input
              id="post-tricks"
              type="text"
              value={newPostTrickInput}
              onChange={(e) => setNewPostTrickInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTrick(newPostTrickInput); } }}
              placeholder="Type a trick and press Enter"
              className={FIELD_INPUT}
            />
            {newPostTricks.length > 0 && (
              <div className="mt-2.5 flex flex-wrap gap-2">
                {newPostTricks.map((trick) => (
                  <button key={trick} type="button" onClick={() => removeTrick(trick)} className="flex items-center gap-1.5 rounded-full bg-lime-400/15 py-1 pl-3 pr-2 text-xs font-bold text-lime-100 transition hover:bg-lime-400/25" aria-label={`Remove ${trick}`}>
                    {trick} <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Location */}
          <div>
            <span className={FIELD_LABEL}>Location <span className="font-medium normal-case tracking-normal text-gray-500">(optional)</span></span>
            {locationTag ? (
              <div className="flex items-center justify-between gap-2 rounded-lg bg-white/[0.07] px-4 py-3">
                <div className="flex min-w-0 items-center gap-2.5">
                  <MapPinIcon className="h-5 w-5 shrink-0 text-white" />
                  <span className="truncate text-sm font-bold text-white">{locationTag.name}</span>
                </div>
                <button type="button" onClick={() => setLocationTag(null)} className="shrink-0 rounded-full p-1 text-gray-300 hover:bg-white/10" aria-label="Remove location">
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => onRequestLocationPick({ mediaItems, caption: newPostCaption, tricks: newPostTricks, sport: newPostSport, locationTag, overlays: overlaysByUrl })}
                className="flex w-full items-center gap-2.5 rounded-lg bg-white/[0.07] px-4 py-3 text-sm font-semibold text-gray-300 transition hover:bg-white/10 hover:text-white"
              >
                <MapPinIcon className="h-5 w-5" /> Add location
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Sticky action bar */}
      <div
        className="border-t border-white/10 bg-black px-4 pt-3"
        style={{ paddingBottom: 'calc(var(--sab-native, env(safe-area-inset-bottom)) + 0.75rem)' }}
      >
        <div className="mx-auto w-full max-w-[560px]">
          <button
            onClick={handleCreatePost}
            disabled={!mediaItems.length || isPosting}
            className="flex w-full items-center justify-center rounded-full bg-lime-500 py-3.5 text-sm font-black uppercase tracking-[0.08em] text-black shadow-lg shadow-lime-500/20 transition hover:bg-lime-400 active:scale-[0.99] disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-gray-500 disabled:shadow-none"
          >
            {isPosting ? 'Posting…' : mediaItems.length ? 'Post' : 'Select a photo or video'}
          </button>
        </div>
      </div>
      {isEditing && mediaItem && (
        <MediaEditor
          mediaUrl={mediaItem.previewUrl}
          mediaType={isVideo ? 'video' : 'image'}
          initial={overlaysByUrl[mediaItem.previewUrl] ?? null}
          onCancel={() => setIsEditing(false)}
          onDone={(o) => {
            setOverlaysByUrl((prev) => {
              const next = { ...prev };
              if (o) next[mediaItem.previewUrl] = o; else delete next[mediaItem.previewUrl];
              return next;
            });
            setIsEditing(false);
          }}
        />
      )}
    </div>
  );
};

export default UploadPostPage;
